/**
 * MetricTooltip Component
 *
 * Provides contextual help for dashboard metrics via tooltips.
 * Shows a help cursor on hover and displays an explanation tooltip.
 *
 * Usage:
 * <MetricTooltip label="Avg Retrieval" value="450ms" tooltip="Average time to search...">
 *   Avg Retrieval: 450ms
 * </MetricTooltip>
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';

export interface MetricTooltipProps {
  /** The label for the metric (e.g., "Avg Retrieval") */
  label: string;
  /** The formatted value (e.g., "450ms") */
  value: string;
  /** The tooltip explanation text */
  tooltip: string;
}

/**
 * Wraps a metric label and value with a help tooltip.
 * Displays a subtle help icon and shows explanation on hover.
 */
export function MetricTooltip({ label, value, tooltip }: MetricTooltipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help">
          <span>
            {label}: {value}
          </span>
          <HelpCircle
            className="h-3 w-3 text-muted-foreground/60"
            aria-hidden="true"
          />
          <span className="sr-only">(hover for explanation)</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}
